import { computed, Signal, inject, untracked } from "@angular/core";
import { signalStore, withState, withComputed, withMethods, withProps, patchState } from "@ngrx/signals";
import { AccessLevels, isMinFls } from "../../pojo/Fls";
import { RoleRights } from "./securityConfig/role.rights";
import { S25Util } from "../../util/s25-util";
import { LoginService } from "../../services/login.service";
import { RoleRightsConfigService } from "./securityConfig/role.rights.config.service";
import { FlsStore } from "../../stores/fls.store";
import { LoginStore } from "../../stores/login.store";
import { ProductLicenseStore } from "../../stores/product.license.store";
import { S25Const } from "../../util/s25-const";

/**
 * Internal state for the security store
 */
interface SeSecurityState {
    initialized: boolean;
    initializing: boolean;
    maxRights: RoleRights.RightsAccess;
    _orgRights: Record<number, RoleRights.RightsAccess | Promise<unknown>>;
    /** Active workflow todos for current user; used for My Workflow nav + badge */
    assignedWorkflowPendingCount: number;
}

const initialState: SeSecurityState = {
    initialized: false,
    initializing: false,
    maxRights: {} as RoleRights.RightsAccess,
    _orgRights: {},
    assignedWorkflowPendingCount: 0,
};

/**
 * Signal store for Student Engagement security checks
 * Provides reactive permission checking with automatic caching
 *
 * Usage:
 * - Inject in components: `securityStore = inject(SeSecurityStore)`
 * - Imperative checks: `await this.securityStore.hasMinAccessForOrg(...)`
 * - Reactive signals: `canEdit = this.securityStore.orgAccessSignal(...)`
 * - SE admin / route-style gate: `this.securityStore.canSeAdmin()`
 */
export const SeSecurityStore = signalStore(
    { providedIn: "root" }, // Now provided at root for AngularJS bridge access

    withState(initialState),
    withProps(() => ({
        flsStore: inject(FlsStore),
        loginStore: inject(LoginStore),
        productLicenseStore: inject(ProductLicenseStore),
    })),
    withComputed((store) => ({
        // Use FlsStore for FLS checks - reactive and already cached
        hasFLSOverride: computed(() => store.flsStore.hasFull("STUDENT_ENGAGEMENT")),
        /** Matches backend {@code Series25AuthUser.isSeAdmin()} for org status config admin UI. */
        canConfigureOrgStatus: computed(
            () => store.flsStore.hasFull("STUDENT_ENGAGEMENT") || store.loginStore.user()?.securityGroupId === -1,
        ),
        /** Instance SE admin gate for Connect25 system alerts CRUD (ANG-7007); keep checks here only. */
        canConfigureSystemAlerts: computed(() => store.flsStore.hasFull("STUDENT_ENGAGEMENT")),
        /** Institution Connect25 email template admin (matches EMAIL_TEMPLATE_INSTITUTION_ADMIN / SE FLS Full). */
        canConfigureEmailTemplates: computed(() => store.flsStore.hasFull("STUDENT_ENGAGEMENT")),
        canEditOrgRating: computed(
            () =>
                store.loginStore.loggedIn() &&
                store.flsStore.hasFull("STUDENT_ENGAGEMENT") &&
                store.flsStore.hasFull("CU_ACCT_RATING"),
        ),
        hasReadFls: computed(() => store.flsStore.hasRead("STUDENT_ENGAGEMENT")),
        /** Logged in with at least Read FLS on Student Engagement (e.g. elections admin). */
        canSeAdmin: computed(() => store.loginStore.loggedIn() && store.flsStore.hasRead("STUDENT_ENGAGEMENT")),
        /** Matches 25Live chat gate: CHAT read FLS, SOC enabled, sys-admin bypass. */
        canAccessChat: computed(
            () =>
                store.loginStore.loggedIn() &&
                S25Const.socEnabled &&
                (store.loginStore.user()?.securityGroupId === -1 || store.flsStore.hasRead("CHAT")),
        ),
        canViewElections: computed(() => {
            if (store.flsStore.hasFull("STUDENT_ENGAGEMENT")) {
                return true;
            }
            return (
                store.loginStore.loggedIn() &&
                isMinFls(store.maxRights()[RoleRights.RoleRightsList.SE_ELECTION_MANAGE], AccessLevels.Read)
            );
        }),
        canManageElectionsInstitutionWide: computed(() => {
            if (store.flsStore.hasFull("STUDENT_ENGAGEMENT")) {
                return true;
            }
            return (
                store.loginStore.loggedIn() &&
                isMinFls(store.maxRights()[RoleRights.RoleRightsList.SE_ELECTION_MANAGE], AccessLevels.Full)
            );
        }),
        studentEngagementFls: computed(() => store.flsStore.accessLevel("STUDENT_ENGAGEMENT")),
        isInitialized: computed(() => store.initialized()),
        loadingOrgs: computed(
            () =>
                new Set(
                    Object.entries(store._orgRights())
                        .filter(([_, rights]) => rights instanceof Promise)
                        .map(([orgId]) => Number(orgId)),
                ),
        ),
        loadedOrgs: computed(
            () =>
                new Map<number, RoleRights.RightsAccess>(
                    Object.entries(store._orgRights())
                        .filter(([_, rights]) => !(rights instanceof Promise))
                        .map(([orgId, rights]) => [Number(orgId), rights as RoleRights.RightsAccess]),
                ),
        ),
    })),
    withMethods((store) => ({
        /**
         * Ensure the store is initialized (lazy loading)
         * Loads max rights on first call
         * FLS is handled reactively by FlsStore
         * Subsequent calls return immediately if already initialized
         * @public Can be called externally to preload security data
         */
        async ensureInitialized(): Promise<void> {
            // Already initialized, return immediately
            if (store.initialized()) {
                return;
            }

            // Already initializing, wait for it to complete
            // Untracked to prevent infinite loop when updating store.initializing below
            if (untracked(() => store.initializing())) {
                await S25Util.waitFor(() => store.initialized(), 50);
                return;
            }

            // Start initialization
            patchState(store, { initializing: true });

            try {
                const isLoggedIn = await LoginService.getLoggedIn();
                await store.productLicenseStore.ensureLoaded();
                if (!isLoggedIn || !store.productLicenseStore.hasStudentEngagement()) {
                    // User not logged in or Student Engagement not enabled
                    patchState(store, {
                        initialized: true,
                        initializing: false,
                        maxRights: {} as RoleRights.RightsAccess,
                        assignedWorkflowPendingCount: 0,
                    });
                    return;
                }

                // Fetch max rights (FLS is handled by FlsStore)
                const maxRights = await RoleRightsConfigService.getMaxRights();

                patchState(store, {
                    initialized: true,
                    initializing: false,
                    maxRights,
                });
            } catch (error) {
                // Reset initializing flag on error so it can be retried
                patchState(store, { initializing: false });
                throw error;
            }
        },
    })),
    withMethods((store) => {
        /**
         * Fetch rights for given orgIDs, patching the store to include the loading
         * state and then the concrete response values
         */
        async function fetchAndPatchOrgRights(orgIds: number[]): Promise<Record<number, RoleRights.RightsAccess>> {
            const fetchRequest = RoleRightsConfigService.getOrgRights(orgIds);
            // Store the new promise into each org ID to mark as loading
            patchState(store, {
                _orgRights: {
                    ...store._orgRights(),
                    ...Object.fromEntries(orgIds.map((id) => [id, fetchRequest])),
                },
            });

            try {
                const orgRights = await fetchRequest;
                // add the concrete values to the store
                patchState(store, {
                    _orgRights: {
                        ...store._orgRights(),
                        ...orgRights,
                    },
                });
                return orgRights;
            } catch (error) {
                // remove the fetchRequest from the store
                patchState(store, {
                    _orgRights: Object.fromEntries(
                        Object.entries(store._orgRights()).filter(([_, rights]) => rights === fetchRequest),
                    ),
                });
                console.error("Failed to load org rights:", error);
                return Promise.reject(error);
            }
        }

        const methods = {
            /**
             * Load org-specific rights for given organization IDs
             * Only fetches orgs that haven't been loaded yet
             * Caches results to prevent duplicate requests
             */
            async loadAllOrgRights(orgIds?: number[]): Promise<Map<number, RoleRights.RightsAccess> | undefined> {
                if (!orgIds?.length) {
                    return;
                }

                await store.ensureInitialized();

                const isLoggedIn = await LoginService.getLoggedIn();
                await store.productLicenseStore.ensureLoaded();
                if (!isLoggedIn || !store.productLicenseStore.hasStudentEngagement()) {
                    // User not logged in or Student Engagement not enabled - return early without loading orgs
                    return;
                }

                const orgsToFetch = orgIds.filter((id) => !store.loadedOrgs().has(id) && !store.loadingOrgs().has(id));

                await Promise.allSettled([
                    orgsToFetch.length ? fetchAndPatchOrgRights(orgsToFetch) : Promise.resolve(),
                    ...orgIds.map((id) => store._orgRights()[id]).map(S25Util.asPromise),
                ]);
                return new Map(
                    orgIds.filter((id) => store.loadedOrgs().has(id)).map((id) => [id, store.loadedOrgs().get(id)!]),
                );
            },
            /**
             * Convenience method to load a single organization's rights
             */
            async getOrgRights(orgId: number): Promise<RoleRights.RightsAccess> {
                // delegating to the getAll ensures permission checks are run
                const result = await methods.loadAllOrgRights([orgId]);
                return result?.get(orgId)!;
            },
        };
        return methods;
    }),
    withMethods((store) => {
        return {
            /**
             * Check if user has at least minimum access to a right for a specific organization
             * Includes FLS checks - FLS override grants access regardless of role rights
             *
             * @param rightName The right to check
             * @param organizationId The organization ID to check
             * @param minLevel Minimum access level required (defaults to Read)
             * @returns Promise<boolean> true if user has at least minLevel access or has FLS override
             */
            async hasMinAccessForOrg(
                rightName: RoleRights.RoleRightsList,
                organizationId: number,
                minLevel: AccessLevels = AccessLevels.Read,
            ): Promise<boolean> {
                await store.ensureInitialized();

                // Check FLS override first
                if (store.hasFLSOverride()) {
                    return true;
                }

                const orgRights = await store.getOrgRights(organizationId);
                return isMinFls(orgRights?.[rightName], minLevel);
            },

            /**
             * Check if user has access to ANY of the specified rights for specified organizations or FLS override
             *
             * @param rightNames Array of rights to check
             * @param orgIds Array of organization IDs to check
             * @param minLevel Minimum access level required (defaults to Read)
             * @returns Promise<Record<number, boolean>> Map of orgId -> boolean (true if user has access to ANY right or has FLS override)
             */
            async hasAccessForAny(
                rightNames: RoleRights.RoleRightsList[],
                orgIds: number[],
                minLevel: AccessLevels = AccessLevels.Read,
            ): Promise<Record<number, boolean>> {
                await store.ensureInitialized();

                // Check FLS override first
                if (store.hasFLSOverride()) {
                    const result: Record<number, boolean> = {};
                    orgIds.forEach((orgId) => {
                        result[orgId] = true;
                    });
                    return result;
                }

                // Check each org
                await store.loadAllOrgRights(orgIds);
                const result: Record<number, boolean> = {};

                orgIds.forEach((orgId) => {
                    const orgRights = store.loadedOrgs().get(orgId);
                    if (!orgRights) {
                        result[orgId] = false;
                    } else {
                        result[orgId] = rightNames.some((rightName) => isMinFls(orgRights[rightName], minLevel));
                    }
                });

                return result;
            },

            /**
             * Create a reactive signal that tracks org access permission
             * The signal automatically updates when the org ID signal changes
             *
             * @param orgIdSignal Signal or function returning the organization ID
             * @param rightName The right to check
             * @param minLevel Minimum access level required
             * @returns Signal<boolean | undefined> - undefined while loading, true/false once loaded
             */
            orgAccessSignal(
                orgIdSignal: Signal<number | string | undefined>,
                rightName: RoleRights.RoleRightsList,
                minLevel: AccessLevels = AccessLevels.Read,
            ): Signal<boolean | undefined> {
                return computed(() => {
                    // Check FLS override
                    if (store.hasFLSOverride()) {
                        return true;
                    }

                    const orgId = Number(orgIdSignal());

                    if (!orgId) return undefined;

                    // If not initialized, trigger initialization and return undefined
                    if (!store.initialized()) {
                        untracked(store.ensureInitialized).catch((error: unknown) => {
                            console.error("Failed to initialize security store:", error);
                        });
                        return undefined;
                    }

                    // If org not loaded, trigger load and return undefined
                    if (!store.loadedOrgs().has(orgId)) {
                        store.getOrgRights(orgId);
                        return undefined;
                    }

                    return isMinFls(store.loadedOrgs().get(orgId)?.[rightName], minLevel);
                });
            },
        };
    }),
    withMethods((store) => ({
        /**
         * Get SE role rights for AngularJS permission checks
         * Returns maxRights after ensuring store is initialized
         * License check is handled internally by ensureInitialized()
         */
        async getSeRoleRights(): Promise<{ maxRights: RoleRights.RightsAccess }> {
            await store.ensureInitialized();
            return { maxRights: store.maxRights() };
        },

        setAssignedWorkflowPendingCount(count: number): void {
            patchState(store, { assignedWorkflowPendingCount: count });
        },
    })),
);
